/**
 * The assistant's conversation-history list — shared by the planner sidebar
 * and the drawer's history view. Visual language adapted from aurora's chat
 * ConversationItem (ListItemButton + selected highlight + relative time),
 * minus the avatar — this is a companion, not a person chat, and it reads
 * calmer without one.
 *
 * The highlighted item is the ACTIVE conversation (the one both chat
 * surfaces render); selecting any other resumes it via activate.
 */

import { Box, CircularProgress, List, ListItemButton, Typography } from '@mui/material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/el';
import { useTranslation } from 'react-i18next';
import { AssistantConversationSummaryDto } from 'services/api/assistant';

dayjs.extend(relativeTime);

interface AssistantHistoryListProps {
  conversations: AssistantConversationSummaryDto[];
  loading: boolean;
  error?: unknown;
  /** Blocks switching while a reply streams or an activation is in flight. */
  disabled?: boolean;
  onSelect: (conversationId: string) => void;
}

const AssistantHistoryList = ({
  conversations,
  loading,
  error,
  disabled,
  onSelect,
}: AssistantHistoryListProps) => {
  const { t, i18n } = useTranslation();
  // i18n.language may be hyphen-less ("elGR") — normalize before dayjs.
  const dateLocale = (i18n.language || 'en').startsWith('el') ? 'el' : 'en';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error || conversations.length === 0) {
    return (
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', display: 'block', px: 2, py: 3, textAlign: 'center' }}
      >
        {t(error ? 'assistant.history.error' : 'assistant.history.empty')}
      </Typography>
    );
  }

  return (
    <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {conversations.map((conversation) => (
        <ListItemButton
          key={conversation.id}
          selected={conversation.isActive}
          disabled={disabled}
          onClick={() => onSelect(conversation.id)}
          sx={{ borderRadius: 2, px: 1.5, py: 1, display: 'block' }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {conversation.title ?? conversation.preview ?? t('assistant.history.untitled')}
          </Typography>
          {conversation.lastMessageAt && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {dayjs(conversation.lastMessageAt).locale(dateLocale).fromNow()}
            </Typography>
          )}
        </ListItemButton>
      ))}
    </List>
  );
};

export default AssistantHistoryList;
